import type { Score } from '@/music/score/types';
import type { PlaylistTuneRef } from '@/storage/playlists';
import type { FeedbackContext } from './feedback';

/** Never attach auth fragments, query strings, or invite/share tokens. */
export function feedbackRoute(path: string): string {
  return path
    .split(/[?#]/)[0]
    .replace(/\/(join|setlist)\/[^/]+/g, '/$1/[link]')
    .slice(0, 500);
}

export function feedbackContext(
  route: string,
  score?: Score,
  ref?: PlaylistTuneRef | null,
  bar?: number,
): FeedbackContext {
  const context: FeedbackContext = {
    route: feedbackRoute(route),
    build:
      document.querySelector<HTMLMetaElement>('meta[name="build"]')?.content.slice(0, 120) ??
      'development',
    browser: navigator.userAgent.slice(0, 1000),
  };
  if (!score) return context;
  const index = bar === undefined ? 0 : bar - 1;
  let key = score.form[0]?.keySig;
  let time = score.form[0]?.timeSig;
  for (let i = 0; i <= Math.min(index, score.form.length - 1); i++) {
    key = score.form[i]?.keySig ?? key;
    time = score.form[i]?.timeSig ?? time;
  }
  const measure = bar === undefined ? undefined : score.form[index];
  context.tune = {
    id: (score.id ?? '').slice(0, 300),
    title: (score.title.trim() || 'Untitled tune').slice(0, 300),
    composer: score.composer?.slice(0, 300),
    source: ref?.kind,
    revisionId: ref?.kind === 'score' ? ref.revisionId : undefined,
    key: key ? `${key.tonic} ${key.mode}` : undefined,
    timeSignature: time,
    barCount: score.form.length,
    bar,
    // One written bar, including the melody. No entire score, playlist,
    // console, local storage or account data is copied into the report.
    measure: measure
      ? {
          form: JSON.parse(JSON.stringify(measure)) as Record<string, unknown>,
          parts: score.parts
            .filter((part) => part.measures[index])
            .map((part) => ({
              name: part.name.slice(0, 300),
              measure: JSON.parse(JSON.stringify(part.measures[index])) as Record<string, unknown>,
            })),
        }
      : undefined,
  };
  return context;
}
